'use client'

import { useState, useEffect } from 'react'

import { LoaderCircle } from 'lucide-react'
import ReactPlayer from 'react-player'

import { LevelFooter } from '../Footer'
import { HUD } from '../HUD'
import { LevelToolbar } from '../Toolbar'

import { useLevelContext } from '@/features/level-play'
import { ChallengeStep, useFlow } from '@/shared/lib/steps-flow'
import { showErrorAlert } from '@/shared/lib/ui'
import { Button } from '@/shared/ui/Button'

interface GameVideoProps {
  challengeId: number
  step?: ChallengeStep
}

export const GameVideo = ({ challengeId }: GameVideoProps) => {
  const { goNext, enableContinue, verifyChallenge } = useFlow()
  const { level, isSubmitting, onChallengeAnswer, getMinimumAdvanceStatus } =
    useLevelContext()

  const challenge = level?.active_challenges.find(c => c.id === challengeId)
  const minimumAdvance = getMinimumAdvanceStatus(challengeId)
  const meta = (challenge?.meta ?? {}) as Record<string, unknown>

  const videoUrl =
    typeof meta['video_url'] === 'string'
      ? (meta['video_url'] as string)
      : undefined
  const nextButtonEnabled = Boolean(meta['next_button_enabled'] ?? false)
  // const advanceEnabled = Boolean(meta['advance_enabled'] ?? false) // não usado

  const [canContinue, setCanContinue] = useState(false)

  const handleContinue = async () => {
    if (!challenge) return
    try {
      const { ok } = await onChallengeAnswer({
        uid: challenge.uid,
        answer: 'Finalizado',
        skipValidation: true
      })

      if (ok) enableContinue()
      verifyChallenge({ correct: true })
      goNext()
    } catch (error) {
      showErrorAlert(error)
    }
  }

  useEffect(() => {
    if (nextButtonEnabled) {
      const id = setTimeout(() => setCanContinue(true), 3000)
      return () => clearTimeout(id)
    }
  }, [nextButtonEnabled])

  return (
    <HUD.Card>
      <HUD.Header challenge={challenge} />
      <HUD.Toolbar>
        <LevelToolbar />
      </HUD.Toolbar>

      <HUD.Body className="flex flex-col gap-4">
        <HUD.Description challenge={challenge} />

        <div className="aspect-square bg-gray-100 rounded-xl overflow-hidden">
          <ReactPlayer
            url={videoUrl}
            controls
            width="100%"
            height="100%"
            loop={false}
            onEnded={() => setCanContinue(true)}
            config={{
              youtube: {
                playerVars: {
                  autoplay: 0,
                  rel: 0,
                  showinfo: 0,
                  modestbranding: 1,
                  disablekb: 1,
                  iv_load_policy: 3
                }
              }
            }}
          />
        </div>

        {!canContinue && !nextButtonEnabled && !isSubmitting && (
          <p className="text-center text-sm text-gray-500">
            Assista ao vídeo até o final para poder continuar.
          </p>
        )}
      </HUD.Body>

      <LevelFooter>
        <Button
          type="button"
          onClick={handleContinue}
          className="w-full font-bold relative z-10"
          disabled={!canContinue || minimumAdvance.isLocked || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <LoaderCircle className="size-5 animate-spin" />
              <span>Carregando</span>
            </>
          ) : (
            <span>
              {minimumAdvance.isLocked
                ? `Aguarde ${minimumAdvance.label}`
                : 'Continuar'}
            </span>
          )}
        </Button>
      </LevelFooter>
    </HUD.Card>
  )
}
